import React from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, XCircle } from 'lucide-react';
import { CDMO_IMPORT_MESSAGES } from '../../constants/messages';
import type { CdmoImportResult } from '../../lib/cdmoImport/importRows';
import { OutcomeBadge } from './shared';

const editLink = (id: string) => `/cdmo-application?edit=${id}`;

const ResultStep: React.FC<{ result: CdmoImportResult }> = ({ result }) => {
  const { counts } = result;
  const skipped = counts.skipped_duplicate + counts.skipped_error + counts.skipped_by_admin;
  const allGood = counts.created > 0 && counts.failed === 0;

  const tiles = [
    { label: 'Created', value: counts.created, className: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
    { label: 'Skipped', value: skipped, className: 'border-slate-200 bg-slate-50 text-slate-800' },
    { label: 'Failed', value: counts.failed, className: 'border-red-200 bg-red-50 text-red-800' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4">
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
            allGood ? 'bg-emerald-50 text-emerald-700' : counts.created === 0 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {allGood ? <CheckCircle2 className="w-7 h-7" aria-hidden /> : counts.created === 0 ? <XCircle className="w-7 h-7" aria-hidden /> : <AlertTriangle className="w-7 h-7" aria-hidden />}
        </div>
        <div>
          <h4 className="text-lg font-bold text-charcoal">
            {counts.created} CDMO profile{counts.created === 1 ? '' : 's'} created
          </h4>
          <p className="text-sm text-slate-gray">
            Created as Unclaimed. No emails were sent — invite them from Campaign when you are ready.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {tiles.map((tile) => (
          <div key={tile.label} className={`rounded-xl border px-4 py-3 ${tile.className}`}>
            <p className="text-2xl font-bold tabular-nums">{tile.value}</p>
            <p className="text-xs font-medium uppercase tracking-wide opacity-80">{tile.label}</p>
          </div>
        ))}
      </div>

      {result.auditFailures.length > 0 && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
            {CDMO_IMPORT_MESSAGES.AUDIT_FAILED_TITLE}
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1 break-words">
            {result.auditFailures.slice(0, 5).map((failure) => (
              <li key={failure}>{failure}</li>
            ))}
          </ul>
          {result.auditFailures.length > 5 && <p className="mt-1">…and {result.auditFailures.length - 5} more.</p>}
        </div>
      )}

      <div className="rounded-xl border border-slate-gray/15 overflow-hidden">
        {/* `relative`: keeps any absolutely positioned cell content inside the scroll box on narrow screens. */}
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-light-gray text-left text-xs font-semibold uppercase tracking-wide text-slate-gray">
              <tr>
                <th scope="col" className="w-14 px-3 py-3">Row</th>
                <th scope="col" className="px-2 py-3">Company</th>
                <th scope="col" className="px-2 py-3">Outcome</th>
                <th scope="col" className="px-2 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-gray/10 bg-white">
              {result.results.map((row) => (
                <tr key={row.rowNumber} className="align-top">
                  <td className="px-3 py-3 text-slate-gray tabular-nums">{row.rowNumber}</td>
                  <td className="px-2 py-3 font-medium text-charcoal max-w-[16rem] break-words">{row.companyName || 'Unnamed row'}</td>
                  <td className="px-2 py-3"><OutcomeBadge outcome={row.outcome} /></td>
                  <td className="px-2 py-3 text-slate-gray break-words">
                    {row.outcome === 'created' && row.cdmoId ? (
                      <a
                        href={editLink(row.cdmoId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-semibold text-selected-blue hover:underline"
                      >
                        Open profile <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                      </a>
                    ) : (
                      <>
                        <span>{row.reason ?? '—'}</span>
                        {row.existingId && (
                          <a
                            href={editLink(row.existingId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-1 font-semibold text-selected-blue hover:underline whitespace-nowrap"
                          >
                            Existing profile <ExternalLink className="w-3.5 h-3.5" aria-hidden />
                          </a>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResultStep;
